import { ReactNode, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Button } from "@/components/wpl/button/src";
import { TextInput } from "@/components/wpl/text-input/src";
import { Link } from "@/libraries/design/components";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const contentSectionCss = css({
    padding: "0 20px",
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
    display: "flex",
    flexDirection: "column",
    textAlign: "center"
});

const headerStyle = css({
    fontSize: "4.8rem",
    fontFamily:
        "'Charlie Display', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontWeight: 500,
    /* eslint-disable-next-line local/ensure-wac-token-usage */
    color: "#253858",
    [media.only.md]: {
        marginBlockEnd: "4.5rem"
    },
    [media.below.md]: {
        marginBlockEnd: "3.5rem"
    },
    height: "3.5rem;"
});

const textStyle = css({
    fontFamily:
        "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontWeight: 400,
    fontSize: "1rem",
    margin: "0 0 24px",
    /* eslint-disable-next-line local/ensure-wac-token-usage */
    color: "#091e42",
    verticalAlign: "baseline",
    lineHeight: 1.5
});

const linkTextStyle = css({
    fontFamily:
        "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontWeight: 400,
    fontSize: "1rem",
    margin: "0 0",
    /* eslint-disable-next-line local/ensure-wac-token-usage */
    color: "#091e42",
    verticalAlign: "baseline",
    lineHeight: 1.5
});

const searchMainContainerStyle = css({
    insetBlockStart: "100%",
    insetInlineStart: token("space.0"),
    lineHeight: token("space.300")
});
const searchFormStyle = css({
    lineHeight: token("space.300"),
    maxWidth: "1200px",
    margin: "0 auto",
    position: "relative",
    [media.above.sm]: {
        padding: "0px 20px 24px 20px"
    }
});
const searchContainerStyle = css({
    textAlign: "center",
    margin: `${token("space.0")} auto`,
    [media.below.md]: {
        paddingBlockStart: 0
    }
});
const searchInputContainerStyle = css({
    display: "inline-block",
    marginInlineEnd: token("space.100"),
    textAlign: "left",
    width: "250px"
});
const divStyle = css({
    [media.above.md]: {
        marginBlockStart: 0
    }
});
const searchIconButtonStyle = css({
    "backgroundColor": "#0052cc",
    "backgroundSize": token("space.200"),
    "border": "1px solid #0052cc",
    "borderRadius": token("space.050"),
    "cursor": "pointer",
    "verticalAlign": "bottom",

    "color": "#ffffff",
    "padding": `${token("space.0")} ${token("space.100")}`,
    "marginBlockEnd": token("space.075"),
    "&:visited": {
        /* eslint-disable-next-line local/ensure-wac-token-usage */
        color: "#f4f5f7"
    },
    "&:hover, &:active": {
        /* eslint-disable-next-line local/ensure-wac-token-usage */
        backgroundColor: "#0049b0",
        /* eslint-disable-next-line local/ensure-wac-token-usage */
        border: "1px solid #0049b0"
    }
});

export const MainSection = () => {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchText, setSearchText] = useState("");
    const sorry = intl.formatMessage(
        {
            id: "404.sorry",
            defaultMessage: "Sorry, the page you're looking for cannot be found"
        },
        {
            ...DEFAULT_VALUES
        }
    );

    const visitHomepage = intl.formatMessage(
        {
            id: "404.visitHomepage",
            defaultMessage:
                "Visit our <HomepageLink>homepage</HomepageLink>, get <HelpLink>help</HelpLink>, or try searching"
        },
        {
            ...DEFAULT_VALUES,
            HomepageLink: (text: ReactNode) => (
                <Link
                    text={text as unknown as string}
                    aria-label={text as unknown as string}
                    href={useLocalizedHref("/")}
                    type={"inline"}
                />
            ),
            HelpLink: (text: ReactNode) => (
                <Link
                    text={text as unknown as string}
                    aria-label={text as unknown as string}
                    href={useLocalizedHref("/resources/support")}
                    type={"inline"}
                />
            )
        }
    );

    const search = intl.formatMessage({
        id: "404.search",
        defaultMessage: "Search"
    });

    const mainCss = css({
        paddingBlockStart: "4rem",
        paddingBlockEnd: "6rem",

        [media.above.md]: {
            marginBlockStart: 0,
            padding: "12px 0px 128px 0px"
        },

        [media.below.sm]: {
            padding: "20px 0px 96px 0px"
        },

        [media.only.sm]: {
            padding: "48px 0px 140px 0px"
        }
    });

    return (
        <div css={mainCss}>
            <div css={contentSectionCss}>
                <h1 css={headerStyle}>404</h1>
                <p css={textStyle}>{sorry}</p>
                <p css={linkTextStyle}>{visitHomepage}</p>
            </div>
            <div css={divStyle}>
                <Row layout="8">
                    <div
                        css={searchMainContainerStyle}
                        id="wac-search"
                        data-testid="search-container"
                    >
                        <form
                            css={searchFormStyle}
                            action={useLocalizedHref(`/search`)}
                            method="GET"
                            id="wac-search-form"
                            data-testid="search-form"
                        >
                            <div
                                id="wac-search-container"
                                css={searchContainerStyle}
                            >
                                <div
                                    css={searchInputContainerStyle}
                                    id="wac-search-input-container"
                                >
                                    <TextInput
                                        ref={inputRef}
                                        className="fullWidth"
                                        placeholder={""}
                                        label={""}
                                        name="q"
                                        id="search-input"
                                        value={searchText}
                                        onChangeHandler={setSearchText}
                                        data-testid="search-input"
                                        aria-label="Search Input"
                                    />
                                </div>
                                <Button
                                    text={search}
                                    type={"primary"}
                                    size={"small"}
                                    css={searchIconButtonStyle}
                                />
                            </div>
                        </form>
                    </div>
                </Row>
            </div>
        </div>
    );
};
